import { useMutation } from '@tanstack/react-query';
import { createContext, useContext, useState, type ReactNode } from 'react';
import {
  Alert,
  Button,
  DocumentCapture,
  Fact,
  Facts,
  Icon,
  Modal,
  ModalActions,
  Select,
  type CapturedDocument,
} from '@evogard/ui';
import { DESK_BAND_LABELS, format as formatMoney, fromColumn } from '@evogard/domain';
import type { DeskBoardCard, DeskOperator, DeskSummary } from '@evogard/contract';
import { api, ok } from '../../../lib/api.js';
import { attends, cardPlace, destinationOf, type BoardCard } from './deskBoard.js';
import { useDeskFocus } from './deskFocus.js';
import { useInvalidateDesk } from './useDesk.js';

/** Os atos que tiram o card do lugar — cada um passa por este diálogo. */
export type DeskMove =
  | { kind: 'pull' }
  | { kind: 'pass'; operator: DeskOperator }
  | { kind: 'queue' }
  | { kind: 'autoPaid' }
  | { kind: 'collectionPaid' }
  | { kind: 'discard' }
  | { kind: 'reopen' }
  | { kind: 'credit' };

interface PendingMove {
  card: BoardCard;
  move: DeskMove;
  summary: DeskSummary;
  onShown?: () => void;
}

const DeskMoveContext = createContext<(pending: PendingMove) => void>(() => undefined);

/**
 * Onde o diálogo do movimento mora: no quadro, e não dentro do card. O card
 * sai da coluna assim que o quadro recarrega — com o diálogo dentro dele, o
 * aviso de "movido" sumiria junto, no instante em que deveria aparecer.
 */
export function DeskMoveProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingMove | null>(null);
  return (
    <DeskMoveContext.Provider value={setPending}>
      {children}
      {pending && (
        <DeskMoveDialog
          key={`${pending.card.tenant.slug}-${pending.card.id}-${pending.move.kind}`}
          {...pending}
          onClose={() => setPending(null)}
        />
      )}
    </DeskMoveContext.Provider>
  );
}

/** Abre o diálogo de um movimento — `start({ card, move, summary })`. */
export function useDeskMove(): (pending: PendingMove) => void {
  return useContext(DeskMoveContext);
}

/**
 * Confirmar um movimento do card, e mostrar onde ele foi parar.
 *
 * No legado todo movimento passava pelo "Confirmar Transferência — Você tem
 * certeza?" (`App.tsx:6626`), e a Mesa nasceu sem ele: um "Devolver à fila"
 * saía no clique, sem a pessoa perceber (relato de 20/10/2026). Aqui o diálogo
 * não pergunta só "tem certeza?": diz de onde o card sai, para onde vai e o que
 * muda — quem fica com ele, a quem vai o crédito —, que é o que quem confirma
 * precisa ler.
 *
 * Depois do sim, o mesmo diálogo mostra o card movido, com a coluna que o
 * SERVIDOR respondeu (`card.column`), e não a que a tela imaginou; e "Mostrar
 * no quadro" leva até ele.
 */
function DeskMoveDialog({
  card,
  move,
  summary,
  onClose,
  onShown,
}: {
  card: BoardCard;
  move: DeskMove;
  summary: DeskSummary;
  onClose: () => void;
  /** Depois de "Mostrar no quadro" — a lateral fecha junto, para o quadro aparecer. */
  onShown?: () => void;
}) {
  const invalidate = useInvalidateDesk();
  const focus = useDeskFocus();
  const me = summary.me;
  const wide = summary.can.distribute;
  const meAttends = attends(summary, me, card.flow);
  const myName = summary.operators.find((o) => o.userId === me)?.name ?? 'você';
  const listed = summary.operators.filter((o) => o.listed);
  const sameFlow = listed.filter((o) => (card.flow === 'receptiva' ? o.receptiveFlow : o.activeFlow));

  /* A quem o servidor credita o pago se ninguém disser: o dono, ou quem clicou se atende. */
  const defaultCredit = card.operator?.id ?? (meAttends ? me : null);
  const [proof, setProof] = useState<CapturedDocument | null>(null);
  const [creditTo, setCreditTo] = useState(defaultCredit ?? '');

  const params = { tenant: card.tenant.slug, id: card.id };
  const fileOf = (d: CapturedDocument) => ({ nome: d.nome, typeMime: d.typeMime, content: d.content });

  const run = useMutation({
    mutationFn: async (): Promise<DeskBoardCard> => {
      switch (move.kind) {
        case 'pull':
          return ok(await api.billingDesk.pull({ params, body: {} }), 200, 'Não foi possível puxar o card.');
        case 'pass':
          return ok(
            await api.billingDesk.handOver({ params, body: { operatorId: move.operator.userId } }),
            200,
            'Não foi possível passar o card.',
          );
        case 'queue':
          return ok(
            await api.billingDesk.handOver({ params, body: { operatorId: null } }),
            200,
            'Não foi possível devolver o card à fila mãe.',
          );
        case 'autoPaid':
          return ok(
            await api.billingDesk.pay({ params, body: { kind: 'pago_automatico' } }),
            200,
            'Não foi possível dar o card como pago.',
          );
        case 'collectionPaid': {
          if (!proof) throw new Error('Anexe o comprovante do pagamento.');
          const chosen = creditTo && creditTo !== defaultCredit ? creditTo : null;
          return ok(
            await api.billingDesk.pay({
              params,
              body: { kind: 'pago_cobranca', proof: fileOf(proof), operatorId: chosen },
            }),
            200,
            'Não foi possível dar o card como pago.',
          );
        }
        case 'discard':
          return ok(
            await api.billingDesk.discard({ params, body: {} }),
            200,
            'Não foi possível tirar o card do quadro.',
          );
        case 'reopen':
          return ok(await api.billingDesk.reopen({ params, body: {} }), 200, 'Não foi possível reabrir o card.');
        case 'credit':
          return ok(
            await api.billingDesk.credit({
              params,
              body: { operatorId: creditTo, proof: proof ? fileOf(proof) : null },
            }),
            200,
            'Não foi possível creditar o pagamento.',
          );
      }
    },
    onSuccess: () => invalidate(),
  });

  const name = card.name ?? 'O card';
  const plan = planOf(card, move, { myName, meAttends });
  const result = run.data;

  /* Os campos que alguns atos pedem antes do sim. */
  let fields: ReactNode = null;
  let ready = true;
  if (move.kind === 'collectionPaid') {
    ready = proof !== null && (defaultCredit !== null || creditTo !== '');
    fields = (
      <>
        {wide && (
          <Select label="Quem cobrou" value={creditTo} onChange={(e) => setCreditTo(e.target.value)}>
            {defaultCredit === null && <option value="">Escolha quem cobrou…</option>}
            {sameFlow.map((o) => (
              <option key={o.userId} value={o.userId}>
                {o.name}
              </option>
            ))}
            {card.operator && !sameFlow.some((o) => o.userId === card.operator!.id) && (
              <option value={card.operator.id}>{card.operator.name}</option>
            )}
          </Select>
        )}
        <DocumentCapture
          label="Comprovante do pagamento"
          value={proof}
          onChange={setProof}
          acceptsPdf
          disabled={run.isPending}
          help="Pago cobrança exige o comprovante: foto ou PDF."
        />
      </>
    );
  }
  if (move.kind === 'credit') {
    const needsProof = card.outcome === 'pago_automatico';
    ready = creditTo !== '' && (!needsProof || proof !== null);
    fields = (
      <>
        <Select label="Creditar a" value={creditTo} onChange={(e) => setCreditTo(e.target.value)}>
          <option value="">Escolha…</option>
          {listed.map((o) => (
            <option key={o.userId} value={o.userId}>
              {o.name}
            </option>
          ))}
        </Select>
        <DocumentCapture
          label={needsProof ? 'Comprovante (obrigatório)' : 'Novo comprovante (opcional)'}
          value={proof}
          onChange={setProof}
          acceptsPdf
          disabled={run.isPending}
        />
      </>
    );
  }

  const credited =
    move.kind === 'collectionPaid'
      ? (sameFlow.find((o) => o.userId === creditTo)?.name ?? card.operator?.name ?? null)
      : move.kind === 'credit'
        ? (listed.find((o) => o.userId === creditTo)?.name ?? null)
        : null;

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={result ? plan.done : plan.title}
      size="md"
      dismissOnOutsideClick={!run.isPending}
    >
      {result ? (
        <div className="flex flex-col gap-5">
          <div role="status" className="flex flex-col items-center gap-3 py-2 text-center">
            <span className="grid size-16 place-items-center rounded-full bg-sucesso-suave text-sucesso animate-in fade-in-0 zoom-in-50 duration-500">
              <Icon nome="confirmado" size={24} className="size-8" />
            </span>
            <p className="text-sm text-texto">
              {result.column === null ? (
                <>
                  <strong>{name}</strong> saiu do quadro.
                </>
              ) : (
                <>
                  <strong>{name}</strong> agora está em{' '}
                  <strong>{cardPlace(result)}</strong>.
                </>
              )}
            </p>
          </div>
          <ModalActions>
            <Button variant="contorno" onClick={onClose}>
              Fechar
            </Button>
            {result.column !== null && (
              <Button
                icon="quadro"
                onClick={() => {
                  focus.show({ ...result, tenant: card.tenant });
                  onShown?.();
                  onClose();
                }}
              >
                Mostrar no quadro
              </Button>
            )}
          </ModalActions>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <CardLine card={card} />
          <Facts>
            <Fact label="Está em">{cardPlace(card)}</Fact>
            <Fact label="Vai para">
              <span className="font-semibold text-texto">{plan.to}</span>
            </Fact>
          </Facts>
          <p className="text-sm text-texto">
            {plan.consequence}
            {credited && move.kind === 'collectionPaid' && <> A recuperação fica no nome de {credited}.</>}
            {credited && move.kind === 'credit' && <> O crédito passa a ser de {credited}.</>}
          </p>
          {fields}
          {run.error && <Alert tone="erro">{run.error.message}</Alert>}
          <ModalActions>
            <Button variant="contorno" onClick={onClose} disabled={run.isPending}>
              Cancelar
            </Button>
            <Button
              variant={plan.danger ? 'perigo' : 'primario'}
              loading={run.isPending}
              disabled={!ready}
              onClick={() => run.mutate()}
            >
              {plan.confirm}
            </Button>
          </ModalActions>
        </div>
      )}
    </Modal>
  );
}

/** Quem é o card, numa linha — para a pessoa conferir que é o card certo. */
function CardLine({ card }: { card: BoardCard }) {
  const amount = card.amount === null ? null : fromColumn(card.amount);
  const parts = [
    card.plates.join(', ') || null,
    amount === null ? null : formatMoney(amount),
    card.tenant.slug.toUpperCase(),
  ].filter((p): p is string => p !== null);
  return (
    <div className="rounded-borda bg-superficie-suave px-3 py-2">
      <p className="text-sm font-semibold text-texto">{card.name ?? 'Sem nome'}</p>
      <p className="text-xs text-texto-suave">{parts.join(' · ')}</p>
    </div>
  );
}

interface Plan {
  title: string;
  confirm: string;
  done: string;
  to: string;
  consequence: string;
  danger?: boolean;
}

/** O que cada ato diz antes do sim — o destino e o que muda. */
function planOf(
  card: BoardCard,
  move: DeskMove,
  { myName, meAttends }: { myName: string; meAttends: boolean },
): Plan {
  const band = card.band === null ? null : DESK_BAND_LABELS[card.band];
  switch (move.kind) {
    case 'pull':
      return {
        title: 'Puxar o atendimento',
        confirm: 'Puxar para mim',
        done: 'Atendimento puxado',
        to: destinationOf(card, { name: myName }),
        consequence:
          'O card passa a ser seu e sai da fila mãe: ninguém mais o puxa. Ele fica com você até ser pago, ser passado a alguém ou mudar de faixa pelo vencimento.',
      };
    case 'pass':
      return {
        title: 'Passar o card',
        confirm: `Passar para ${move.operator.name}`,
        done: 'Card passado',
        to: destinationOf(card, move.operator),
        consequence:
          card.flow === 'receptiva'
            ? `${move.operator.name} passa a atender este card na receptiva.`
            : `${move.operator.name} passa a atender este card. A coluna é a da faixa do vencimento${band ? ` (${band})` : ''}: ela vem do boleto, e não se escolhe.`,
      };
    case 'queue':
      return {
        title: 'Devolver à fila mãe',
        confirm: 'Devolver à fila mãe',
        done: 'Card devolvido à fila mãe',
        to: destinationOf(card, null),
        consequence: `${card.operator ? `${card.operator.name} deixa de atender este card. ` : ''}${
          card.band?.startsWith('vencimento_')
            ? 'Na fila mãe de vencimento, qualquer operador pode puxá-lo.'
            : 'Na fila mãe de atraso, quem distribui reparte.'
        }`,
      };
    case 'autoPaid':
      return {
        title: 'Dar como pago automático',
        confirm: 'Dar como pago automático',
        done: 'Card dado como pago',
        to: 'Pago automaticamente',
        consequence: card.operator
          ? `O card sai do quadro de trabalho. O pagamento fica no nome de ${card.operator.name}.`
          : meAttends
            ? 'O card sai da fila mãe. O pagamento fica no seu nome.'
            : 'O card sai da fila mãe sem crédito para ninguém, como o pago que a casa reconhece sozinha pela baixa do boleto.',
      };
    case 'collectionPaid':
      return {
        title: 'Dar como pago cobrança',
        confirm: 'Dar como pago cobrança',
        done: 'Card dado como pago',
        to: 'Pago cobrança',
        consequence: 'O card sai do quadro de trabalho e conta como recuperação da cobrança.',
      };
    case 'discard':
      return {
        title: 'Tirar do quadro',
        confirm: 'Tirar do quadro',
        done: 'Card tirado do quadro',
        to: 'Fora do quadro',
        consequence:
          'O card sai do quadro e da busca. Nada é apagado: o registro e o histórico ficam guardados.',
        danger: true,
      };
    case 'reopen':
      return {
        title: 'Devolver ao quadro',
        confirm: 'Devolver ao quadro',
        done: 'Card de volta ao quadro',
        to: card.flow === 'receptiva' ? destinationOf(card, card.operator) : destinationOf(card, null),
        consequence:
          card.flow === 'receptiva'
            ? 'O card volta para quem o atendia, na receptiva.'
            : 'O card volta sem dono, para a fila mãe da faixa do vencimento.',
      };
    case 'credit':
      return {
        title: card.outcome === 'pago_automatico' ? 'Passar para pago cobrança' : 'Trocar o crédito',
        confirm: card.outcome === 'pago_automatico' ? 'Passar para pago cobrança' : 'Trocar o crédito',
        done: 'Crédito trocado',
        to: 'Pago cobrança',
        consequence:
          card.outcome === 'pago_automatico'
            ? 'O pagamento passa a contar como recuperação da cobrança, com o comprovante.'
            : 'O card continua em pago cobrança.',
      };
  }
}
